/**
 * sessionPrefill — the router-state contract that pre-fills the C Mode
 * session wizard (SPEC-04 §4/§5, docs/9-exploration/c-mode/).
 *
 * An entry point that already has a decision's ingredients on screen — a
 * whiteboard reveal, a saved board, a catchball Request — navigates:
 *
 *   navigate('/prioritization/sessions/new', { state: { prefill } })
 *
 * The wizard seeds its steps from this and nothing else: `ask` fills step ①,
 * `items` become pre-selected platform items in step ② (submitted with
 * source:'platform' so the session ranks the real rows — identity preserved
 * from the wall to the Plan of Record), and `exchange` renders the SPEC-01 §3
 * exchange line on the confirmation card. The wizard never loads stores to
 * enrich a prefill; whoever displays the Request supplies the names/dates it
 * already has (view-layer only, Doc 26 rule 7).
 *
 * Router state is untyped and survives back/forward navigation, so
 * parseSessionPrefill validates defensively; a refresh drops the state and
 * the wizard starts blank — the correct degradation.
 */

import type { RevealScript } from './whiteboardReveal';
import { METHOD_REGISTRY } from './methodRegistry';

export type SessionPrefillOrigin = 'whiteboard_import' | 'whiteboard_board';

export interface SessionPrefillItem {
  /** Platform item id — the same row the import committed. */
  id: string;
  title: string;
}

/** The SPEC-01 §3 exchange context: creation in response to a Request. */
export interface SessionPrefillExchange {
  cycleId: string;
  loopId: string;
  /** The specific catchball Ball being answered, when the entry point knows it. */
  ballId?: string;
  /** Who asked — supplied by the entry point that displayed the Request. */
  evaluatorName?: string;
  /** ISO date the response is due, when the Request carries one. */
  dueDate?: string;
}

export interface SessionPrefill {
  /** Seeds step ① "What are you deciding?" (also the session name). */
  ask?: string;
  /** Pre-selected platform items for step ②. */
  items?: SessionPrefillItem[];
  origin?: SessionPrefillOrigin;
  exchange?: SessionPrefillExchange;
  /**
   * A method already chosen before the Designer opened — e.g. one of the
   * recommended tools offered after a configuration was picked. Becomes the
   * ranking step's (S5) tool and the session's selected tool.
   */
  toolId?: string;
  /** The prioritization context the session belongs to (prioritization_sessions.context_id). */
  contextId?: string;
  /** Every tool the configuration recommended, in order; `toolId` is the first. */
  recommendedToolIds?: string[];
}

const MAX_ASK = 200;

/** Human caption for the step-② pre-selected block. */
export function prefillOriginLabel(origin: SessionPrefillOrigin | undefined): string {
  switch (origin) {
    case 'whiteboard_import':
    case 'whiteboard_board':
      return 'From your whiteboard';
    default:
      return 'Pre-selected';
  }
}

/**
 * Build a prefill from a whiteboard reveal script: the board title becomes the
 * ask, every revealed node becomes a pre-selected platform item (deduped —
 * two boxes matched to the same existing item collapse to one).
 */
export function sessionPrefillFromReveal(
  script: RevealScript,
  origin: SessionPrefillOrigin,
): SessionPrefill {
  const seen = new Set<string>();
  const items: SessionPrefillItem[] = [];
  for (const n of script.nodes) {
    if (seen.has(n.itemId)) continue;
    seen.add(n.itemId);
    items.push({ id: n.itemId, title: n.title });
  }
  return {
    ask: `Rank the priorities from “${script.boardTitle}”`.slice(0, MAX_ASK),
    items,
    origin,
  };
}

/**
 * Validate untyped router state into a SessionPrefill, or null when there is
 * nothing usable. Tolerates junk field-by-field: a garbled `items` entry is
 * dropped, a garbled `exchange` is ignored, a usable `ask` still applies.
 */
export function parseSessionPrefill(state: unknown): SessionPrefill | null {
  if (!state || typeof state !== 'object') return null;
  const raw = (state as { prefill?: unknown }).prefill;
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const out: SessionPrefill = {};

  if (typeof p.ask === 'string' && p.ask.trim()) out.ask = p.ask.trim().slice(0, MAX_ASK);

  if (Array.isArray(p.items)) {
    const seen = new Set<string>();
    const items: SessionPrefillItem[] = [];
    for (const entry of p.items) {
      if (!entry || typeof entry !== 'object') continue;
      const { id, title } = entry as { id?: unknown; title?: unknown };
      if (typeof id !== 'string' || !id || typeof title !== 'string' || !title.trim()) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      items.push({ id, title: title.trim() });
    }
    if (items.length > 0) out.items = items;
  }

  if (p.origin === 'whiteboard_import' || p.origin === 'whiteboard_board') out.origin = p.origin;

  if (typeof p.toolId === 'string' && p.toolId.trim()) out.toolId = p.toolId.trim();
  if (typeof p.contextId === 'string' && p.contextId.trim()) out.contextId = p.contextId.trim();
  if (Array.isArray(p.recommendedToolIds)) {
    const ids = p.recommendedToolIds.filter((t): t is string => typeof t === 'string' && t.trim() !== '');
    if (ids.length > 0) out.recommendedToolIds = ids;
  }

  if (p.exchange && typeof p.exchange === 'object') {
    const ex = p.exchange as Record<string, unknown>;
    if (typeof ex.cycleId === 'string' && ex.cycleId && typeof ex.loopId === 'string' && ex.loopId) {
      out.exchange = {
        cycleId: ex.cycleId,
        loopId: ex.loopId,
        ...(typeof ex.ballId === 'string' && ex.ballId ? { ballId: ex.ballId } : {}),
        ...(typeof ex.evaluatorName === 'string' && ex.evaluatorName.trim()
          ? { evaluatorName: ex.evaluatorName.trim() }
          : {}),
        ...(typeof ex.dueDate === 'string' && ex.dueDate ? { dueDate: ex.dueDate } : {}),
      };
    }
  }

  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Build a prefill from the catchball query params the Catchball Inbox puts on its
 * /prioritization/sessions/new links (`?loopId=&cycleId=[&ballId=]`, built in
 * CatchballInboxPage). URL arrivals carry exchange ids only — never ask or items:
 * a URL is shareable and unbounded, so rich data rides router state instead.
 * Returns null when the URL names no exchange.
 */
export function sessionPrefillFromSearchParams(params: URLSearchParams): SessionPrefill | null {
  const loopId = params.get('loopId');
  const cycleId = params.get('cycleId');
  if (!loopId || !cycleId) return null;
  const ballId = params.get('ballId');
  return { exchange: { cycleId, loopId, ...(ballId ? { ballId } : {}) } };
}

/**
 * The one resolution order for every /sessions/new consumer: rich router
 * state first (whiteboard CTAs, mobile senders), then the Catchball Inbox's query
 * params. Keeps desktop and mobile arrivals on a single contract.
 */
export function resolveSessionPrefill(state: unknown, params: URLSearchParams): SessionPrefill | null {
  return parseSessionPrefill(state) ?? sessionPrefillFromSearchParams(params);
}

/** The cycle and track a new session is started from inside. */
export interface SessionArrivalFrame {
  cycleId: string;
  trackId: string | null;
  /** 'link' = the link the person clicked named it; 'focus' = the cycle or track they are in. */
  source: 'link' | 'focus';
}

/**
 * Where a new session is being started from, so the Designer relates it to
 * that cycle and track without asking (owner ruling 2026-11-03). In order:
 * the link the person clicked (`?cycleId=&trackId=`, or a Request's exchange
 * in router state), then the cycle and track the app's focus is on — focus
 * follows the page (useFocusFollowsInstance), so it names the cycle or track
 * the person was just in. A link naming the cycle alone takes the focused
 * track when it is in that cycle. Null when started from nowhere in particular.
 */
export function resolveSessionArrivalFrame(
  params: URLSearchParams,
  prefill: SessionPrefill | null,
  focus: { pinnedCycleId: string | null; pinnedTrackId: string | null },
): SessionArrivalFrame | null {
  const linkCycleId = params.get('cycleId') ?? prefill?.exchange?.cycleId ?? null;
  const linkTrackId = params.get('trackId');
  if (linkCycleId) {
    const focusTrack = focus.pinnedCycleId === linkCycleId ? focus.pinnedTrackId : null;
    return { cycleId: linkCycleId, trackId: linkTrackId ?? focusTrack, source: 'link' };
  }
  if (focus.pinnedCycleId) {
    return { cycleId: focus.pinnedCycleId, trackId: focus.pinnedTrackId, source: 'focus' };
  }
  return null;
}

/** The SPEC-01 §3 exchange line for the confirmation card, or null. */
export function exchangeLine(exchange: SessionPrefillExchange | undefined): string | null {
  if (!exchange) return null;
  const who = exchange.evaluatorName ? `${exchange.evaluatorName}’s ask` : 'an open request';
  const due = exchange.dueDate
    ? ` · response due ${new Date(exchange.dueDate).toLocaleDateString()}`
    : '';
  return `This session answers ${who}${due}.`;
}

/**
 * The configuration picker's hand-off (ConfigurationSelector navigates to
 * `/prioritization/sessions?context_id=…&recommended_tools=…&autolaunch=1`),
 * read into the prefill the Event Designer takes: the context, and the
 * recommended tools resolved through the method registry — the first one
 * chosen, the rest named. Null when the URL carries no context.
 */
export function sessionPrefillFromConfigurationHandoff(params: URLSearchParams): SessionPrefill | null {
  const contextId = params.get('context_id');
  if (!contextId) return null;
  const toolIds = (params.get('recommended_tools') ?? '')
    .split(',')
    .filter(Boolean)
    .map((slug) => {
      const match = METHOD_REGISTRY.find((m) => m.variants.some((v) => v.toolId === slug) || m.methodId === slug);
      return match ? match.variants[0]?.toolId ?? match.methodId : null;
    })
    .filter((t): t is string => t !== null);
  const recommendedToolIds = Array.from(new Set(toolIds));
  return {
    contextId,
    ...(recommendedToolIds.length > 0 ? { toolId: recommendedToolIds[0], recommendedToolIds } : {}),
  };
}
